/**
 * Catalog state as of 2026-05-01. Only models that are actively supported
 * AND not on a published deprecation timeline within the next 60 days are
 * listed. Update this file when:
 *   • a new flagship model lands (e.g. Grok 4.30, Gemini 3.5)
 *   • a model receives a deprecation notice — drop it here so users don't
 *     accidentally pin a doomed pin.
 */
export const LLM_CATALOG = {
    openai: {
        provider: 'openai',
        label: 'OpenAI',
        freeForm: false,
        defaultModelId: 'gpt-4.1-mini',
        docsUrl: 'https://platform.openai.com/docs/models',
        voiceAgentNative: true,
        models: [
            {
                id: 'gpt-4.1-mini',
                label: 'GPT-4.1 mini',
                ttftP50Ms: 220,
                inputUsdPerMTok: 0.4,
                outputUsdPerMTok: 1.6,
                supportsTools: true,
                note: 'Default — beste Latenz/Preis-Balance für Voice.',
            },
            {
                id: 'gpt-4.1',
                label: 'GPT-4.1',
                ttftP50Ms: 400,
                inputUsdPerMTok: 2,
                outputUsdPerMTok: 8,
                supportsTools: true,
            },
            {
                id: 'gpt-4o-mini',
                label: 'GPT-4o mini',
                ttftP50Ms: 250,
                inputUsdPerMTok: 0.15,
                outputUsdPerMTok: 0.6,
                supportsTools: true,
            },
            {
                id: 'gpt-4o',
                label: 'GPT-4o',
                ttftP50Ms: 350,
                inputUsdPerMTok: 2.5,
                outputUsdPerMTok: 10,
                supportsTools: true,
            },
            {
                id: 'gpt-realtime',
                label: 'GPT Realtime (Speech-to-Speech)',
                ttftP50Ms: 200,
                supportsTools: true,
                hasVoiceAgent: true,
                note: 'Native Voice-Agent. Nutzt eine eigene Realtime-Pipeline statt ElevenLabs-TTS.',
            },
        ],
    },
    anthropic: {
        provider: 'anthropic',
        label: 'Anthropic',
        freeForm: false,
        // Aliase verifiziert per `POST /v1/messages` (2026-05-01).
        defaultModelId: 'claude-haiku-4-5',
        docsUrl: 'https://docs.anthropic.com/en/docs/about-claude/models',
        models: [
            {
                id: 'claude-haiku-4-5',
                label: 'Claude Haiku 4.5',
                ttftP50Ms: 280,
                inputUsdPerMTok: 1,
                outputUsdPerMTok: 5,
                supportsTools: true,
                note: 'Default — schnell + günstig, guter Voice-Default.',
            },
            {
                id: 'claude-sonnet-4-5',
                label: 'Claude Sonnet 4.5',
                ttftP50Ms: 440,
                inputUsdPerMTok: 3,
                outputUsdPerMTok: 15,
                supportsTools: true,
            },
            {
                id: 'claude-sonnet-4-6',
                label: 'Claude Sonnet 4.6',
                ttftP50Ms: 450,
                inputUsdPerMTok: 3,
                outputUsdPerMTok: 15,
                supportsTools: true,
            },
            {
                id: 'claude-opus-4-5',
                label: 'Claude Opus 4.5',
                ttftP50Ms: 680,
                inputUsdPerMTok: 15,
                outputUsdPerMTok: 75,
                supportsTools: true,
            },
            {
                id: 'claude-opus-4-6',
                label: 'Claude Opus 4.6',
                ttftP50Ms: 690,
                inputUsdPerMTok: 15,
                outputUsdPerMTok: 75,
                supportsTools: true,
            },
            {
                id: 'claude-opus-4-7',
                label: 'Claude Opus 4.7',
                ttftP50Ms: 700,
                inputUsdPerMTok: 15,
                outputUsdPerMTok: 75,
                supportsTools: true,
                note: 'Höchste Qualität — meist zu langsam für Live-Voice.',
            },
        ],
    },
    google: {
        provider: 'google',
        label: 'Google (Gemini)',
        freeForm: false,
        // IDs verifiziert per `GET /v1beta/models?key=…` (2026-05-01).
        // Stabile Modelle: gemini-2.5-*. Die Gemini-3-Reihe ist aktuell noch
        // als `-preview` markiert und kann jederzeit deprecated werden.
        defaultModelId: 'gemini-2.5-flash',
        docsUrl: 'https://ai.google.dev/gemini-api/docs/models',
        voiceAgentNative: true,
        models: [
            {
                id: 'gemini-2.5-flash',
                label: 'Gemini 2.5 Flash',
                ttftP50Ms: 220,
                inputUsdPerMTok: 0.3,
                outputUsdPerMTok: 2.5,
                supportsTools: true,
                note: 'Default — stabil, schnell, ideal für Voice.',
            },
            {
                id: 'gemini-2.5-flash-lite',
                label: 'Gemini 2.5 Flash Lite',
                ttftP50Ms: 180,
                inputUsdPerMTok: 0.1,
                outputUsdPerMTok: 0.4,
                supportsTools: true,
                note: 'Günstigste stabile Variante.',
            },
            {
                id: 'gemini-2.5-pro',
                label: 'Gemini 2.5 Pro',
                ttftP50Ms: 600,
                inputUsdPerMTok: 1.25,
                outputUsdPerMTok: 10,
                supportsTools: true,
            },
            {
                id: 'gemini-3-flash-preview',
                label: 'Gemini 3 Flash (Preview)',
                ttftP50Ms: 230,
                inputUsdPerMTok: 0.3,
                outputUsdPerMTok: 2.5,
                supportsTools: true,
                note: 'Preview — Nachfolger von 2.5 Flash, kann sich ändern.',
            },
            {
                id: 'gemini-3-pro-preview',
                label: 'Gemini 3 Pro (Preview)',
                ttftP50Ms: 620,
                inputUsdPerMTok: 1.5,
                outputUsdPerMTok: 12,
                supportsTools: true,
            },
            {
                id: 'gemini-3.1-pro-preview',
                label: 'Gemini 3.1 Pro (Preview)',
                ttftP50Ms: 660,
                inputUsdPerMTok: 1.8,
                outputUsdPerMTok: 14,
                supportsTools: true,
                note: 'Stärkstes Reasoning-Modell (Preview).',
            },
        ],
    },
    groq: {
        provider: 'groq',
        label: 'Groq',
        freeForm: false,
        // IDs verifiziert per `GET /openai/v1/models` (2026-05-01).
        defaultModelId: 'llama-3.3-70b-versatile',
        docsUrl: 'https://console.groq.com/docs/models',
        models: [
            {
                id: 'llama-3.3-70b-versatile',
                label: 'Llama 3.3 70B Versatile',
                ttftP50Ms: 120,
                inputUsdPerMTok: 0.59,
                outputUsdPerMTok: 0.79,
                supportsTools: true,
                note: 'Default — sehr schnelles TTFT dank Groq-LPU.',
            },
            {
                id: 'llama-3.1-8b-instant',
                label: 'Llama 3.1 8B Instant (kein Tool-Use)',
                ttftP50Ms: 80,
                inputUsdPerMTok: 0.05,
                outputUsdPerMTok: 0.08,
                // Groq listet 8B Instant als „streaming-supported" aber NICHT
                // tool-use-supported. Wenn Pipecat ihm Tools mitgibt, antwortet
                // das Modell oft mit leerem Content → Anrufer hört nur Filler.
                supportsTools: false,
                note: 'Sehr günstig + schnell, aber UNGEEIGNET für Termine/Tickets — kann keine Tools aufrufen. Nur für reine Info-Calls ohne Buchungen wählen.',
            },
            {
                id: 'meta-llama/llama-4-scout-17b-16e-instruct',
                label: 'Llama 4 Scout 17B',
                ttftP50Ms: 110,
                inputUsdPerMTok: 0.1,
                outputUsdPerMTok: 0.4,
                supportsTools: true,
                note: 'Multimodal, 16-Experten-MoE.',
            },
            {
                id: 'openai/gpt-oss-120b',
                label: 'GPT-OSS 120B',
                ttftP50Ms: 140,
                inputUsdPerMTok: 0.15,
                outputUsdPerMTok: 0.6,
                supportsTools: true,
            },
            {
                id: 'openai/gpt-oss-20b',
                label: 'GPT-OSS 20B',
                ttftP50Ms: 100,
                inputUsdPerMTok: 0.1,
                outputUsdPerMTok: 0.3,
                supportsTools: true,
            },
            {
                id: 'qwen/qwen3-32b',
                label: 'Qwen 3 32B',
                ttftP50Ms: 160,
                inputUsdPerMTok: 0.2,
                outputUsdPerMTok: 0.4,
                supportsTools: true,
            },
            {
                id: 'groq/compound',
                label: 'Groq Compound (Agentic)',
                ttftP50Ms: 180,
                inputUsdPerMTok: 0.5,
                outputUsdPerMTok: 1.5,
                supportsTools: true,
                note: 'Built-in Tools (Web-Search, Code-Exec) auf der Groq-Seite.',
            },
            {
                id: 'groq/compound-mini',
                label: 'Groq Compound Mini',
                ttftP50Ms: 140,
                inputUsdPerMTok: 0.2,
                outputUsdPerMTok: 0.6,
                supportsTools: true,
            },
        ],
    },
    mistral: {
        provider: 'mistral',
        label: 'Mistral',
        freeForm: false,
        // IDs verifiziert per `GET /v1/models` (2026-05-01). Mistral Small 4 /
        // Large 3 (Marketing-Namen) gibt's NICHT als API-IDs — man nimmt die
        // `*-latest`-Aliase oder konkrete Datums-Snapshots.
        defaultModelId: 'mistral-medium-latest',
        docsUrl: 'https://docs.mistral.ai/getting-started/models',
        models: [
            {
                id: 'mistral-medium-latest',
                label: 'Mistral Medium',
                ttftP50Ms: 220,
                inputUsdPerMTok: 0.4,
                outputUsdPerMTok: 2,
                supportsTools: true,
                note: 'Default — beste Balance aus Latenz + Qualität für Voice.',
            },
            {
                id: 'mistral-large-latest',
                label: 'Mistral Large',
                ttftP50Ms: 380,
                inputUsdPerMTok: 2,
                outputUsdPerMTok: 6,
                supportsTools: true,
                note: 'Flagship.',
            },
            {
                id: 'mistral-small-latest',
                label: 'Mistral Small',
                ttftP50Ms: 180,
                inputUsdPerMTok: 0.2,
                outputUsdPerMTok: 0.6,
                supportsTools: true,
            },
            {
                id: 'magistral-medium-latest',
                label: 'Magistral Medium (Reasoning)',
                ttftP50Ms: 320,
                inputUsdPerMTok: 1.5,
                outputUsdPerMTok: 5,
                supportsTools: true,
                note: 'Reasoning-spezialisiert.',
            },
            {
                id: 'magistral-small-latest',
                label: 'Magistral Small (Reasoning)',
                ttftP50Ms: 200,
                inputUsdPerMTok: 0.4,
                outputUsdPerMTok: 1.2,
                supportsTools: true,
            },
            {
                id: 'ministral-8b-latest',
                label: 'Ministral 8B',
                ttftP50Ms: 150,
                inputUsdPerMTok: 0.1,
                outputUsdPerMTok: 0.1,
                supportsTools: true,
            },
            {
                id: 'ministral-3b-latest',
                label: 'Ministral 3B',
                ttftP50Ms: 100,
                inputUsdPerMTok: 0.04,
                outputUsdPerMTok: 0.04,
                supportsTools: true,
            },
            {
                id: 'codestral-latest',
                label: 'Codestral (Code)',
                ttftP50Ms: 250,
                inputUsdPerMTok: 0.3,
                outputUsdPerMTok: 0.9,
                supportsTools: true,
                note: 'Auf Code-Tasks getuned — selten für Phone-Voice nötig.',
            },
        ],
    },
    xai: {
        provider: 'xai',
        label: 'xAI (Grok)',
        freeForm: false,
        // IDs verifiziert per `GET https://api.x.ai/v1/models` (2026-05-01).
        // xAI nutzt Bindestriche statt Punkten; Marketing-Namen (z.B. "Grok 4.1
        // Fast") mappen NICHT 1:1 auf API-IDs. Bitte nicht erraten.
        defaultModelId: 'grok-4-1-fast-non-reasoning',
        docsUrl: 'https://docs.x.ai/developers/models',
        voiceAgentNative: true,
        models: [
            {
                id: 'grok-4-1-fast-non-reasoning',
                label: 'Grok 4.1 Fast (Non-Reasoning)',
                ttftP50Ms: 180,
                inputUsdPerMTok: 0.2,
                outputUsdPerMTok: 0.5,
                supportsTools: true,
                note: 'Default — bestes Latenz/Preis-Verhältnis für Voice. Schnell, ohne Reasoning-Overhead.',
            },
            {
                id: 'grok-4-1-fast-reasoning',
                label: 'Grok 4.1 Fast (Reasoning)',
                ttftP50Ms: 280,
                inputUsdPerMTok: 0.2,
                outputUsdPerMTok: 0.5,
                supportsTools: true,
                note: 'Wie 4.1 Fast, aber mit Step-by-Step-Reasoning.',
            },
            {
                id: 'grok-4-fast-non-reasoning',
                label: 'Grok 4 Fast (Non-Reasoning)',
                ttftP50Ms: 200,
                inputUsdPerMTok: 0.5,
                outputUsdPerMTok: 1,
                supportsTools: true,
            },
            {
                id: 'grok-4-fast-reasoning',
                label: 'Grok 4 Fast (Reasoning)',
                ttftP50Ms: 320,
                inputUsdPerMTok: 0.5,
                outputUsdPerMTok: 1,
                supportsTools: true,
            },
            {
                id: 'grok-4-0709',
                label: 'Grok 4',
                ttftP50Ms: 480,
                inputUsdPerMTok: 3,
                outputUsdPerMTok: 15,
                supportsTools: true,
                note: 'Frontier-Reasoning — 256K Kontext.',
            },
            {
                id: 'grok-4.20-0309-non-reasoning',
                label: 'Grok 4.20 (Non-Reasoning)',
                ttftP50Ms: 360,
                inputUsdPerMTok: 3,
                outputUsdPerMTok: 15,
                supportsTools: true,
                note: 'Aktuelles Flagship — 2M-Token-Kontext.',
            },
            {
                id: 'grok-4.20-0309-reasoning',
                label: 'Grok 4.20 (Reasoning)',
                ttftP50Ms: 540,
                inputUsdPerMTok: 3,
                outputUsdPerMTok: 15,
                supportsTools: true,
            },
            {
                id: 'grok-4.20-multi-agent-0309',
                label: 'Grok 4.20 (Multi-Agent)',
                ttftP50Ms: 720,
                inputUsdPerMTok: 5,
                outputUsdPerMTok: 25,
                supportsTools: true,
                note: 'Mehrere Sub-Agents parallel — höchste Qualität, langsamer.',
            },
            {
                id: 'grok-4.3',
                label: 'Grok 4.3',
                ttftP50Ms: 420,
                inputUsdPerMTok: 3,
                outputUsdPerMTok: 15,
                supportsTools: true,
                note: 'Neuestes Modell der 4.x-Reihe (April 2026).',
            },
            {
                id: 'grok-3',
                label: 'Grok 3',
                ttftP50Ms: 420,
                inputUsdPerMTok: 2,
                outputUsdPerMTok: 10,
                supportsTools: true,
            },
            {
                id: 'grok-3-mini',
                label: 'Grok 3 Mini',
                ttftP50Ms: 200,
                inputUsdPerMTok: 0.3,
                outputUsdPerMTok: 0.5,
                supportsTools: true,
            },
            {
                id: 'grok-code-fast-1',
                label: 'Grok Code Fast 1',
                ttftP50Ms: 220,
                inputUsdPerMTok: 0.2,
                outputUsdPerMTok: 1.5,
                supportsTools: true,
                note: 'Auf Code-Tasks getuned.',
            },
            {
                id: 'grok-voice-think-fast-1.0',
                label: 'Grok Voice Think Fast 1.0 (Speech-to-Speech)',
                ttftP50Ms: 180,
                supportsTools: true,
                hasVoiceAgent: true,
                note: 'Native Voice-Agent (Realtime-WS). Wird im "Voice-Agent-Modus → Nativ" gewählt, nicht im Chat-Completions-Picker.',
            },
        ],
    },
    openrouter: {
        provider: 'openrouter',
        label: 'OpenRouter (any model)',
        freeForm: true,
        docsUrl: 'https://openrouter.ai/models',
        models: [
            {
                id: 'anthropic/claude-haiku-4.5',
                label: 'Claude Haiku 4.5 (via OR)',
                supportsTools: true,
            },
            {
                id: 'x-ai/grok-4.20',
                label: 'Grok 4.20 (via OR)',
                supportsTools: true,
            },
            {
                id: 'openai/gpt-oss-120b',
                label: 'GPT-OSS 120B (via OR)',
                supportsTools: true,
            },
            {
                id: 'qwen/qwen-3-72b-instruct',
                label: 'Qwen 3 72B Instruct',
                supportsTools: true,
            },
            {
                id: 'mistralai/mistral-small-4',
                label: 'Mistral Small 4 (via OR)',
                supportsTools: true,
            },
        ],
    },
};
/** Helper for the UI: build a flat list for an `<select>`. */
export function flattenLlmCatalog() {
    const out = [];
    Object.values(LLM_CATALOG).forEach((entry) => {
        entry.models.forEach((m) => out.push({
            provider: entry.provider,
            modelId: m.id,
            label: `${entry.label} · ${m.label}`,
        }));
    });
    return out;
}
export function findModel(provider, modelId) {
    return LLM_CATALOG[provider].models.find((m) => m.id === modelId);
}
/** Providers with a native speech-to-speech voice-agent API. The voice-agent
 *  service uses these to skip the STT/TTS hop when the user opts into
 *  "native voice mode". */
export function providersWithVoiceAgent() {
    return Object.values(LLM_CATALOG)
        .filter((e) => e.voiceAgentNative)
        .map((e) => e.provider);
}
//# sourceMappingURL=llmCatalog.js.map